import { memo } from "react";
import { ReactSVG } from "react-svg";

// Helpers
import { classNames, useTranslate } from "helpers";

// Servisni sloj
import { FLEET_VEHICLE_STATUS_CONFIG } from "services/api/fleet/vehicles/consts";
import { FleetVehicleStatusEnum } from "services/api/fleet/vehicles/types";

import { ActivityChangeListProps, ActivityChangeValueProps } from "./types";

/**
 * Popis izmjena jednog zapisa dnevnika. Svaka izmjena je jedan redak koji se čita kao
 * rečenica: polje, što se s njim dogodilo, pa vrijednosti ("Vozač dodano Ivo Ivić").
 * Bez glagola je iz samih vrijednosti trebalo pogađati je li nešto dodano, promijenjeno
 * ili uklonjeno.
 *
 * Dijele ga vremenska crta i tablica, da se isti podatak ne prikazuje na dva načina.
 * Vrijednosti su tekst proizvoljne duljine (npr. naziv servisa), pa se prelamaju
 * unutar vlastite oznake umjesto da razvlače karticu.
 *
 * Dogovor s backendom: vrijednost u više redaka znači "vrsta u prvom retku, podatak
 * ispod" (tako se šalje lokacija: vrsta premještaja pa gdje je vozilo).
 */
const ActivityChangeList: React.FC<ActivityChangeListProps> = (props) => {
  const { changes, variant = "light" } = props;

  const { t } = useTranslate();

  const isDark = variant === "dark";

  /**
   * Status se u dnevnik upisuje ključem iz baze (npr. `UNAVAILABLE_DAMAGE`), a čita se
   * kao naziv ("Nedostupno – Šteta"). Ostale vrijednosti prolaze nepromijenjene.
   */
  const describeValue = (value: string): string => {
    const config = FLEET_VEHICLE_STATUS_CONFIG[value as FleetVehicleStatusEnum];

    return config ? t(config.label) : value;
  };

  if (changes.length === 0) return <></>;

  return (
    <div className="flex flex-col">
      {changes.map((change, index) => {
        // Glagoli su u srednjem rodu ("dodano", a ne "dodan") — naziv polja može biti
        // bilo kojeg roda, pa se slaganje izbjegava.
        const hasOld = !!change.oldValue;
        const hasNew = !!change.newValue;

        const action = hasOld && hasNew
          ? "promijenjeno iz"
          : hasNew
            ? "dodano"
            : "uklonjeno";

        return (
          <div
            key={index}
            className={classNames(
              "flex flex-wrap items-center gap-x-2 gap-y-1 py-2 text-xs border-b border-dashed first:pt-0 last:pb-0 last:border-0",
              isDark ? "border-slate-600" : "border-slate-200"
            )}
          >
            <span
              className={classNames(
                "text-xs font-medium uppercase",
                isDark ? "text-slate-400" : "text-slate-500"
              )}
            >
              {change.field}
            </span>
            <span className={classNames("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
              {action}
            </span>

            {/* Stara vrijednost se prikazuje samo kad je ima — "— →" je šum, ne podatak. */}
            {hasOld && (
              <>
                <Value text={describeValue(change.oldValue!)} variant={variant} />
                {/* Rečenica se dovršava prijedlogom, ne strelicom: "promijenjeno X u Y". */}
                {hasNew && (
                  <span className={classNames("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                    u
                  </span>
                )}
              </>
            )}

            {hasNew && (
              <Value text={describeValue(change.newValue!)} variant={variant} highlighted />
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Jedna vrijednost izmjene. Zaobljeni pravokutnik podnosi i prelomljeni tekst.
 * Prvi redak višeretčane vrijednosti je vrsta — ispisuje se ispred podatka kao sitna
 * oznaka, da vrijednost ostane u istom retku kao i ostatak rečenice.
 */
const Value: React.FC<ActivityChangeValueProps> = (props) => {
  const { text, highlighted, variant = "light" } = props;

  const [firstLine, ...restLines] = text.split("\n");
  const detail = restLines.join(" ");

  return (
    <span
      className={classNames(
        "inline-flex items-baseline gap-1.5 max-w-full px-2.5 py-1 rounded-full break-words",
        // Nova vrijednost nosi naglasnu boju, stara je prigušena — inače se iz dvije
        // jednake oznake ne vidi koja je vrijednost sada na snazi.
        variant === "dark"
          ? classNames("bg-slate-700", highlighted ? "text-green-400 font-medium" : "text-slate-300")
          : highlighted
            ? "bg-green-50 text-green-700 font-medium"
            : "bg-slate-100 text-slate-500"
      )}
    >
      {/* Kvačica ispred nove vrijednosti — oznaka koja vrijedi i bez razlikovanja boja. */}
      {highlighted && (
        <ReactSVG
          src="/media/icons/check.svg"
          className={classNames(
            "[&_svg]:w-3 [&_svg]:h-3 shrink-0 self-center",
            variant === "dark" ? "[&_path]:stroke-green-400" : "[&_path]:stroke-green-700"
          )}
        />
      )}
      {detail && (
        <span className="text-xs uppercase tracking-wide shrink-0">
          {firstLine}:
        </span>
      )}
      <span className="text-xs">{detail || text}</span>
    </span>
  );
};

export default memo(ActivityChangeList);
